import React, { useContext, useMemo } from 'react';
import { motion } from 'framer-motion';
import { ThemeContext } from '../ThemeContext';
import { useAnnouncementBar } from '../components/AnnouncementBarContext';
import { FileText, Shield, CreditCard, Users, Scale, AlertTriangle } from 'lucide-react';

// Module-level animation state tracking
const cardAnimationState = new Map();

// Card component moved outside to prevent re-creation
const Card = ({ children, className = '', delay = 0, cardId }) => {
  const hasAnimated = cardAnimationState.has(cardId) ? cardAnimationState.get(cardId) : false;

  React.useEffect(() => {
    if (!hasAnimated) {
      cardAnimationState.set(cardId, true);
    }
  }, [cardId, hasAnimated]);

  return (
    <motion.div
      initial={hasAnimated ? false : { opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: hasAnimated ? 0 : delay,
        type: "spring",
        stiffness: 100,
      }}
      whileHover={{
        scale: 1.02,
        y: -5,
        transition: { duration: 0.2 },
      }}
      className={`relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-3xl shadow-2xl p-6 md:p-8 border border-white/20 dark:border-gray-700/50 ${className}`}
      style={{
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      }}
    >
      {children}
    </motion.div>
  );
};

const TermsPage = () => {
  const { isDarkMode } = useContext(ThemeContext);
  const { isVisible: isAnnouncementVisible, height: announcementHeight } = useAnnouncementBar();
  const fixedHeaderOffset = (isAnnouncementVisible ? announcementHeight : 0) + 80;

  // Memoize title gradient to update when dark mode changes
  const titleGradient = useMemo(() => {
    const color = isDarkMode ? '#ff7a00' : '#212D82';
    return `linear-gradient(90deg, ${color} 0%, ${color} 40%, rgba(255, 122, 0, 0.8) 50%, ${color} 60%, ${color} 100%)`;
  }, [isDarkMode]);

  return (
    <div
      className="min-h-screen pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900"
      style={{ paddingTop: `${fixedHeaderOffset}px` }}
    >
      <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6 md:py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring" }}
          className="text-center mb-8 md:mb-12"
        >
          <h1
            key={`terms-title-${isDarkMode}`}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold font-zonaProUltra mb-4 relative inline-block"
            style={{
              background: titleGradient,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shine 10s linear infinite',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
            Terms and Conditions
          </h1>
          <motion.p
            className="text-gray-600 dark:text-gray-400 text-base sm:text-lg md:text-xl font-zonaProRegular"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Terms and Conditions for Game Your SAT
          </motion.p>
        </motion.div>

        <div className="max-w-4xl mx-auto space-y-6 md:space-y-8">
          {/* Section 1: Acceptance of Terms */}
          <Card delay={0.2} cardId="terms-1">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-xl bg-[#06bae4]/10 dark:bg-[#06bae4]/20">
                  <FileText size={24} className="text-[#06bae4]" />
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold font-zonaProUltra text-gray-800 dark:text-gray-200">
                  1. Acceptance of Terms
                </h2>
              </div>
              <p className="text-base md:text-lg text-gray-700 dark:text-gray-300 font-zonaProRegular leading-relaxed text-left">
                By accessing or using the Game Your SAT platform (the "Service"), you agree to be bound by these Terms and Conditions ("Terms") and our Privacy Policy. If you do not agree with any part of these Terms, you may not use the Service. These Terms apply to students, parents/guardians, and B2B partners ("Users").
              </p>
            </motion.div>
          </Card>

          {/* Section 2: The Service */}
          <Card delay={0.3} cardId="terms-2">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-xl bg-[#06bae4]/10 dark:bg-[#06bae4]/20">
                  <Shield size={24} className="text-[#06bae4]" />
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold font-zonaProUltra text-gray-800 dark:text-gray-200">
                  2. The Service: Game Your SAT
                </h2>
              </div>
              <p className="text-base md:text-lg text-gray-700 dark:text-gray-300 font-zonaProRegular leading-relaxed text-left">
                Game Your SAT is a subscription-based, gamified educational platform designed to test knowledge for the SAT exam through adaptive practice, competitive rankings, and structured lessons.
              </p>
            </motion.div>
          </Card>

          {/* Section 3: Subscription, Payment, and Cancellation */}
          <Card delay={0.4} cardId="terms-3">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-xl bg-[#06bae4]/10 dark:bg-[#06bae4]/20">
                  <CreditCard size={24} className="text-[#06bae4]" />
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold font-zonaProUltra text-gray-800 dark:text-gray-200">
                  3. Subscription, Payment, and Cancellation
                </h2>
              </div>
              <div className="space-y-4 text-base md:text-lg text-gray-700 dark:text-gray-300 font-zonaProRegular leading-relaxed text-left">
                <div>
                  <strong className="font-zonaProUltra">Subscription:</strong> To access premium features, including Rank-Up Competitions and the Global Leaderboard, a paid subscription is required.
                </div>
                <div>
                  <strong className="font-zonaProUltra">Billing:</strong> Subscriptions are billed on a recurring monthly, three-month, six-month, or annual basis, as selected at the time of purchase. You agree to pay the subscription fee at the rates in effect for your billing cycle.
                </div>
                <div>
                  <strong className="font-zonaProUltra">Automatic Renewal:</strong> Subscriptions auto-renew at the end of the subscription period for all monthly subscribers, unless you cancel at least 24 hours before the renewal date. (Monthly, three-month and six-month subscriptions) For annual subscriptions, you will receive an email notification of upcoming renewals, which require action on your part to continue service. Annual subscriptions do not auto-renew.
                </div>
                <div>
                  <strong className="font-zonaProUltra">Cancellation:</strong> You may cancel your subscription at any time through your account settings. Cancellation will take effect at the end of the current paid billing cycle, and you will retain access until that date. Refunds are not provided for partial subscription periods.
                </div>
                <div>
                  <strong className="font-zonaProUltra">Price Changes:</strong> We reserve the right to modify subscription fees with thirty (30) days' prior notice.
                </div>
              </div>
            </motion.div>
          </Card>

          {/* Section 4: Competitive Integrity and Cheating Policy */}
          <Card delay={0.5} cardId="terms-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-xl bg-[#06bae4]/10 dark:bg-[#06bae4]/20">
                  <AlertTriangle size={24} className="text-[#06bae4]" />
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold font-zonaProUltra text-gray-800 dark:text-gray-200">
                  4. Competitive Integrity and Cheating Policy
                </h2>
              </div>
              <div className="space-y-4 text-base md:text-lg text-gray-700 dark:text-gray-300 font-zonaProRegular leading-relaxed text-left">
                <p>To maintain the fairness and integrity of Game Your SAT competitions:</p>
                <div>
                  <strong className="font-zonaProUltra">Prohibited Conduct:</strong> Users are prohibited from using unauthorized software, scripts, bots, external tutors, or any mechanical or automated means to gain an unfair advantage in scoring, practice, or competitions.
                </div>
                <div>
                  <strong className="font-zonaProUltra">Score Disqualification:</strong> We reserve the right, at our sole discretion, to investigate, disqualify, suspend, or terminate the account of any user suspected of violating competitive integrity, including removing their Rank Zero Legend status and any accrued rewards.
                </div>
                <div>
                  <strong className="font-zonaProUltra">Accuracy:</strong> While we strive for accuracy, the scores, rankings, and competitive results within the Service are not official and do not guarantee actual SAT performance.
                </div>
              </div>
            </motion.div>
          </Card>

          {/* Section 5: Intellectual Property */}
          <Card delay={0.6} cardId="terms-5">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-xl bg-[#06bae4]/10 dark:bg-[#06bae4]/20">
                  <Shield size={24} className="text-[#06bae4]" />
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold font-zonaProUltra text-gray-800 dark:text-gray-200">
                  5. Intellectual Property (IP)
                </h2>
              </div>
              <div className="space-y-4 text-base md:text-lg text-gray-700 dark:text-gray-300 font-zonaProRegular leading-relaxed text-left">
                <div>
                  <strong className="font-zonaProUltra">Our Content:</strong> All content, design, software, features, graphics, game mechanics, and competitive structures (including the Rank-Up Hub and Engage IQ Platform technology) are the exclusive property of METY Technology and are protected by copyright and intellectual property laws.
                </div>
                <div>
                  <strong className="font-zonaProUltra">Limited License:</strong> We grant you a limited, non-exclusive, non-transferable license to access and use the Service strictly for personal, non-commercial SAT preparation.
                </div>
                <div>
                  <strong className="font-zonaProUltra">User-Generated Content (UGC):</strong> By posting any content (comments, strategy, feedback) to the Rank Up Hub or the Service, you grant us a perpetual, worldwide, royalty-free license to use, modify, publish, and display that content in connection with the Service.
                </div>
              </div>
            </motion.div>
          </Card>

          {/* Section 6: Termination */}
          <Card delay={0.7} cardId="terms-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-xl bg-[#06bae4]/10 dark:bg-[#06bae4]/20">
                  <AlertTriangle size={24} className="text-[#06bae4]" />
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold font-zonaProUltra text-gray-800 dark:text-gray-200">
                  6. Termination
                </h2>
              </div>
              <p className="text-base md:text-lg text-gray-700 dark:text-gray-300 font-zonaProRegular leading-relaxed text-left">
                We may terminate or suspend your access to the Service immediately, without prior notice or liability, for any reason, including without limitation if you breach the Terms.
              </p>
            </motion.div>
          </Card>

          {/* Section 7: Limitation of Liability */}
          <Card delay={0.8} cardId="terms-7">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.0 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-xl bg-[#06bae4]/10 dark:bg-[#06bae4]/20">
                  <Scale size={24} className="text-[#06bae4]" />
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold font-zonaProUltra text-gray-800 dark:text-gray-200">
                  7. Limitation of Liability
                </h2>
              </div>
              <p className="text-base md:text-lg text-gray-700 dark:text-gray-300 font-zonaProRegular leading-relaxed text-left">
                In no event shall METY Technology, Inc., nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential, or punitive damages, including, without limitation, loss of profits, data, use, goodwill, or other intangible losses.
              </p>
            </motion.div>
          </Card>

          {/* Section 8: Governing Law */}
          <Card delay={0.9} cardId="terms-8">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-xl bg-[#06bae4]/10 dark:bg-[#06bae4]/20">
                  <Scale size={24} className="text-[#06bae4]" />
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold font-zonaProUltra text-gray-800 dark:text-gray-200">
                  8. Governing Law
                </h2>
              </div>
              <p className="text-base md:text-lg text-gray-700 dark:text-gray-300 font-zonaProRegular leading-relaxed text-left">
                These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, without regard to its conflict of law provisions.
              </p>
            </motion.div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
